import Phaser from "phaser";
import i18next from "i18next";
import { COLORS, hexToPhaser, darkenColor } from "../../../ui/colors";
import { ModernButton } from "../../../ui/modern-button";
import { BaseWindow } from "../../../ui/windows/base-window";
import { getSafeAreaInsetLeft } from "../../../utils/mobile-utils";
import { shareSquad } from "../../../utils/share-utils";
import { trackEvent } from "../../../utils/ga-events";
import { FriendInfo, FriendRequest } from "../../../models/common";

const SQUAD_CODE_STORAGE_KEY = "squadCode";
const SQUAD_MEMBERS_STORAGE_KEY = "squadMembers";

export class SquadWindow extends BaseWindow {
  private nrFriendRequests: number = 0;

  // --- FRIENDS tab helpers ---
  private addFriendInputContainer: Phaser.GameObjects.Container | null = null;
  private htmlFriendInputElement: HTMLInputElement | null = null;
  private friendsList: Array<FriendInfo> = [];
  private selectedFriend: FriendInfo | null = null;
  private friendsListRequested: boolean = false;
  private friendRequestsList: Array<any> = [];
  private friendRequestsListRequested: boolean = false;

  private createBtn: ModernButton | null = null;
  private joinBtn: ModernButton | null = null;
  private shareBtn: ModernButton | null = null;
  private leaveBtn: ModernButton | null = null;
  private addFriendHeaderBtn: ModernButton | null = null;
  private squadCodeText: Phaser.GameObjects.Text | null = null;
  private buttonSpacing: number = 16;
  private squadCodeInputContainer: Phaser.GameObjects.Container | null = null;
  private htmlInputElement: HTMLInputElement | null = null;
  private tabBar: Phaser.GameObjects.Container | null = null;
  private activeTab: "FRIENDS" | "SQUAD" | "REQUESTS" = "FRIENDS";
  private tabContentContainer: Phaser.GameObjects.Container | null = null;
  private onCloseCallback: (() => void) | null = null;
  private closeButtonObj: ModernButton | null = null;

  constructor(
    scene: Phaser.Scene,
    uiLayer: Phaser.GameObjects.Container,
    private readonly onJoinSubmitted?: () => void,
  ) {
    super(scene);
    this.uiLayer = uiLayer;
  }

  protected getLayoutOverrides(_config: { title: string; side: "left" | "right" | "center" }): Partial<{ x: number; y: number; width: number; height: number }> {
    const gameWidth = this.scene.scale.width;
    const gameHeight = this.scene.scale.height;
    const safeLeft = getSafeAreaInsetLeft();
    const leftMargin = Math.max(100, 16 + safeLeft + 48 + 16);
    const width = gameWidth - leftMargin - 48;
    // Center the window horizontally instead of anchoring it to the left margin.
    const x = (gameWidth - width) / 2;
    const y = 32;
    return { x, y, width, height: gameHeight - y - 20 };
  }

  private getFriends(): Array<FriendInfo> {
    return this.friendsList;
  }

  private showAddFriendInputWindow() {
    this.hideAddFriendInputWindow();

    const gameWidth = this.scene.scale.width;
    const gameHeight = this.scene.scale.height;

    // Full screen semi-transparent background
    const bgOverlay = this.scene.add.rectangle(
      gameWidth / 2,
      gameHeight / 2,
      gameWidth,
      gameHeight,
      hexToPhaser(COLORS.BLACK),
      0.8,
    );
    bgOverlay.setOrigin(0.5);
    bgOverlay.setScrollFactor(0);

    // Center panel
    const panelWidth = Math.min(400, gameWidth - 32);
    const panelHeight = 220;
    const panelX = gameWidth / 2;
    const panelY = gameHeight / 2;
    const cornerRadius = 12;

    const panelBg = this.scene.add.graphics();
    panelBg.fillStyle(hexToPhaser(COLORS.BG), 0.92);
    panelBg.fillRoundedRect(
      panelX - panelWidth / 2,
      panelY - panelHeight / 2,
      panelWidth,
      panelHeight,
      cornerRadius,
    );
    const outerBorderWidth = 4;
    panelBg.lineStyle(outerBorderWidth, hexToPhaser(COLORS.BLACK), 0.8);
    panelBg.strokeRoundedRect(
      panelX - panelWidth / 2,
      panelY - panelHeight / 2,
      panelWidth,
      panelHeight,
      cornerRadius,
    );
    const innerBorderWidth = 4;
    const inset = (outerBorderWidth + innerBorderWidth) / 2;
    const darkerBG = darkenColor(hexToPhaser(COLORS.BG), 0.3);
    panelBg.lineStyle(innerBorderWidth, darkerBG, 0.9);
    panelBg.strokeRoundedRect(
      panelX - panelWidth / 2 + inset,
      panelY - panelHeight / 2 + inset,
      panelWidth - inset * 2,
      panelHeight - inset * 2,
      Math.max(cornerRadius - inset, 4),
    );
    panelBg.setScrollFactor(0);

    // Title
    const title = this.scene.add
      .text(panelX, panelY - panelHeight / 2 + 24, "Add Friend", {
        fontSize: "24px",
        color: COLORS.WHITE,
        fontFamily: "Anton",
        align: "center",
      })
      .setOrigin(0.5, 0);
    title.setScrollFactor(0);

    // Input field
    const inputPadding = 24;
    const inputWidth = panelWidth - inputPadding * 2;
    const inputHeight = 56;
    const inputX = panelX;
    const inputY = panelY - 10;

    this.htmlFriendInputElement = document.createElement("input");
    this.htmlFriendInputElement.type = "text";
    this.htmlFriendInputElement.placeholder = "Enter player name";
    this.htmlFriendInputElement.style.position = "absolute";
    this.htmlFriendInputElement.maxLength = 24;

    const canvas = this.scene.game.canvas;
    const canvasRect = canvas.getBoundingClientRect();
    const scaleX = canvasRect.width / gameWidth;
    const scaleY = canvasRect.height / gameHeight;
    const screenX = canvasRect.left + inputX * scaleX;
    const screenY = canvasRect.top + inputY * scaleY;
    const screenWidth = inputWidth * scaleX;
    const screenHeight = inputHeight * scaleY;

    this.htmlFriendInputElement.style.left = `${screenX - screenWidth / 2}px`;
    this.htmlFriendInputElement.style.top = `${screenY - screenHeight / 2}px`;
    this.htmlFriendInputElement.style.width = `${screenWidth}px`;
    this.htmlFriendInputElement.style.height = `${screenHeight}px`;
    this.htmlFriendInputElement.style.fontSize = `${22 * Math.min(scaleX, scaleY)}px`;
    this.htmlFriendInputElement.style.fontFamily = "Anton, sans-serif";
    this.htmlFriendInputElement.style.backgroundColor = `#${hexToPhaser(COLORS.GRAY).toString(16).padStart(6, "0")}`;
    this.htmlFriendInputElement.style.color = COLORS.WHITE;
    this.htmlFriendInputElement.style.border = `3px solid ${COLORS.WHITE}`;
    this.htmlFriendInputElement.style.padding = "12px";
    this.htmlFriendInputElement.style.textAlign = "center";
    this.htmlFriendInputElement.style.outline = "none";
    this.htmlFriendInputElement.style.zIndex = "100002";
    this.htmlFriendInputElement.style.boxSizing = "border-box";
    this.htmlFriendInputElement.style.textTransform = "uppercase";
    document.body.appendChild(this.htmlFriendInputElement);

    // Buttons
    const btnGap = 16;
    const btnY = panelY + panelHeight / 2 - 40;
    const cancelButton = new ModernButton({
      scene: this.scene,
      x: 0,
      y: btnY,
      text: i18next.t("ui:buttons.cancel") || "CANCEL",
      style: "danger",
      onClick: () => {
        this.hideAddFriendInputWindow();
      },
    });
    cancelButton.setScrollFactor(0);

    const addButton = new ModernButton({
      scene: this.scene,
      x: 0,
      y: btnY,
      text: i18next.t("ui:buttons.addFriend") || "ADD FRIEND",
      style: "success",
      disabled: true,
      onClick: () => {
        const playerName = this.htmlFriendInputElement?.value.trim() || "";
        if (playerName) {
          this.hideAddFriendInputWindow();
          trackEvent("friend-add");
          window.socket?.emit("friend-add", { playerName }, (response: { success: boolean; message: string }) => {
            if (response.success) {
              // Refresh the friends list
              window.socket?.emit("friends-list-request", (listResponse: { success: boolean; friends: Array<FriendInfo> }) => {
                if (listResponse.success) {
                  this.friendsList = listResponse.friends;
                  if (!this.visible) {
                    this.show();
                  } else if (this.activeTab === "FRIENDS") {
                    this.showTabContent();
                  }
                }
              });
            }
          });
        }
      },
    });
    addButton.setScrollFactor(0);

    // Position buttons
    const totalWidth = (cancelButton as any).buttonWidth + btnGap + (addButton as any).buttonWidth;
    const cancelBtnX = panelX - totalWidth / 2 + (cancelButton as any).buttonWidth / 2;
    const addBtnX = panelX + totalWidth / 2 - (addButton as any).buttonWidth / 2;
    cancelButton.setX(cancelBtnX);
    addButton.setX(addBtnX);

    // Validation
    const updateFriendValidation = () => {
      const playerName = this.htmlFriendInputElement?.value || "";
      const isValid = playerName.trim().length >= 3;
      addButton.setDisabled(!isValid);
    };
    this.htmlFriendInputElement.addEventListener("input", updateFriendValidation);
    this.htmlFriendInputElement.addEventListener("keydown", (event: KeyboardEvent) => {
      if (event.key === "Enter") {
        const playerName = this.htmlFriendInputElement?.value.trim() || "";
        if (playerName && playerName.length >= 3) {
          this.hideAddFriendInputWindow();
          trackEvent("friend-add");
          window.socket?.emit("friend-add", { playerName }, (response: { success: boolean; message: string }) => {
            if (response.success) {
              // Refresh the friends list
              window.socket?.emit("friends-list-request", (listResponse: { success: boolean; friends: Array<FriendInfo> }) => {
                if (listResponse.success) {
                  this.friendsList = listResponse.friends;
                  if (!this.visible) {
                    this.show();
                  } else if (this.activeTab === "FRIENDS") {
                    this.showTabContent();
                  }
                }
              });
            }
          });
        }
      }
    });
    setTimeout(() => {
      if (this.htmlFriendInputElement) {
        this.htmlFriendInputElement.focus();
      }
    }, 100);

    // Container
    const containerElements: Phaser.GameObjects.GameObject[] = [
      bgOverlay,
      panelBg,
      title,
      cancelButton,
      addButton,
    ];
    this.addFriendInputContainer = this.scene.add.container(0, 0, containerElements);
    this.addFriendInputContainer.setDepth(100001);
    this.addFriendInputContainer.setVisible(true);
    this.uiLayer.add(this.addFriendInputContainer);
  }

  private hideAddFriendInputWindow() {
    if (this.htmlFriendInputElement) {
      this.htmlFriendInputElement.remove();
      this.htmlFriendInputElement = null;
    }
    if (this.addFriendInputContainer) {
      this.addFriendInputContainer.destroy();
      this.addFriendInputContainer = null;
    }
  }

  private handleDeleteFriend(accountId: number) {
    trackEvent("friend-delete");
    window.socket?.emit("friend-delete", { accountId }, (response: { success: boolean; message: string }) => {
      if (response.success) {
        // Refresh the friends list
        window.socket?.emit("friends-list-request", (listResponse: { success: boolean; friends: Array<FriendInfo> }) => {
          if (listResponse.success) {
            this.friendsList = listResponse.friends;
            this.selectedFriend = null;
            if (!this.visible) {
              this.show();
            } else {
              this.showTabContent();
            }
          }
        });
      }
    });
  }

  private handleShowFriendDetail(friend: FriendInfo) {
    this.selectedFriend = friend;
    this.showTabContent();
  }

  private handleAcceptFriendRequest(accountId: number) {
    trackEvent("friend-request-accept");
    window.socket?.emit("friend-request-accept", { accountId }, (response: { success: boolean; message: string }) => {
      if (response.success) {
        // Refresh the friend requests list
        window.socket?.emit("friend-requests-request", (listResponse: { success: boolean; requests: Array<FriendRequest> }) => {
          if (listResponse.success) {
            this.friendRequestsList = listResponse.requests;
            this.syncFriendRequestDots();
          }
        });
      }
    });
  }

  private handleRejectFriendRequest(accountId: number) {
    trackEvent("friend-request-reject");
    window.socket?.emit("friend-request-reject", { accountId }, (response: { success: boolean; message: string }) => {
      if (response.success) {
        // Refresh the friend requests list
        window.socket?.emit("friend-requests-request", (listResponse: { success: boolean; requests: Array<FriendRequest> }) => {
          if (listResponse.success) {
            this.friendRequestsList = listResponse.requests;
            this.syncFriendRequestDots();
          }
        });
      }
    });
  }

  private syncFriendRequestDots() {
    this.nrFriendRequests = this.friendRequestsList.length;
    this.show(this.onCloseCallback ?? undefined);
  }

  show(onClose?: () => void) {
    this.hide();
    if (!this.scene) return;
    this.onCloseCallback = onClose ?? null;

    trackEvent("squad-window-opened");
    this.scene.events.emit("squad-window-opened");

    // Create base window elements (background, title, close button)
    const baseElements = this.createBaseWindow({
      title: "SOCIAL",
      side: "right",
    });
    const bounds = this.getWindowBounds()!;
    const { x, y, width, height } = bounds;
    const contentStartY = y + 60;
    const contentLeftX = x + this.CONTENT_PADDING_X;

    // --- Tab Bar ---
    if (this.tabBar) {
      this.tabBar.destroy();
      this.tabBar = null;
    }
    const tabNames = ["FRIENDS", "SQUAD", "REQUESTS"];
    // Calculate tab width so all three fit inside window
    const maxTabBarWidth = width - 32; // leave some margin
    const tabWidth = Math.floor((maxTabBarWidth - 2 * 8) / 3); // 8px spacing between tabs
    const tabHeight = 36;
    const tabSpacing = 8;
    const tabBarY = contentStartY;
    const tabBarX = contentLeftX;
    // Improved tab logic: keep references to tab buttons and labels
    const tabButtons: Phaser.GameObjects.Rectangle[] = [];
    const tabLabels: Phaser.GameObjects.Text[] = [];
    const tabDots: Phaser.GameObjects.Image[] = [];
    for (let i = 0; i < tabNames.length; i++) {
      const tabName = tabNames[i];
      const isSelected = this.activeTab === tabName;
      const tabBtn = this.scene.add.rectangle(
        tabBarX + i * (tabWidth + tabSpacing) + tabWidth / 2,
        tabBarY,
        tabWidth,
        tabHeight,
        isSelected ? 0xffe066 : 0x1a3550,
        isSelected ? 0.95 : 0.7,
      );
      tabBtn.setOrigin(0.5, 0);
      tabBtn.setScrollFactor(0);
      tabBtn.setInteractive({ useHandCursor: true });
      tabButtons.push(tabBtn);
      const tabLabel = this.scene.add.text(
        tabBarX + i * (tabWidth + tabSpacing) + tabWidth / 2,
        tabBarY + tabHeight / 2,
        tabName,
        {
          fontSize: "16px",
          color: isSelected ? "#222222" : COLORS.WHITE,
          fontFamily: "Anton",
          align: "center",
        },
      ).setOrigin(0.5);
      tabLabel.setScrollFactor(0);
      tabLabels.push(tabLabel);

      // Badge on REQUESTS tab if there are pending friend requests
      if (tabName === "REQUESTS" && this.nrFriendRequests > 0) {
        const badgeSize = 16;
        const dot = this.scene.add.image(
          tabBarX + i * (tabWidth + tabSpacing) + tabWidth - badgeSize / 2,
          tabBarY,
          "notification-badge",
        );
        dot.setDisplaySize(badgeSize, badgeSize);
        dot.setScrollFactor(0);
        tabDots.push(dot);
      }

      tabBtn.on("pointerdown", () => {
        this.activeTab = tabName as typeof this.activeTab;
        // Reset request flags when switching tabs
        this.friendsListRequested = false;
        this.friendRequestsListRequested = false;
        for (let j = 0; j < tabNames.length; j++) {
          const selected = this.activeTab === tabNames[j];
          tabButtons[j].setFillStyle(selected ? 0xffe066 : 0x1a3550, selected ? 0.95 : 0.7);
          tabLabels[j].setColor(selected ? "#222222" : COLORS.WHITE);
        }
        this.showTabContent();
        // Update ADD FRIEND button visibility when switching tabs
        this.updateAddFriendButtonForTabSwitch();
      });
    }
    this.tabBar = this.scene.add.container(0, 0, [...tabButtons, ...tabLabels, ...tabDots]);
    this.tabBar.setDepth(100001);
    this.tabBar.setScrollFactor(0);

    // --- Tab Content ---
    this.showTabContent();

    // --- Add Friend Header Button (visible when FRIENDS tab is active) ---
    this.updateAddFriendButton(x, width, baseElements);

    // Compose window container
    const windowElements = [
      ...baseElements,
      this.tabBar,
    ];
    if (this.tabContentContainer) {
      windowElements.push(this.tabContentContainer);
    }
    this.container = this.scene.add.container(0, 0, windowElements);
    this.container.setScrollFactor(0);
    this.container.setDepth(100000);
    this.uiLayer.add(this.container);
    this.visible = true;

    this.closeButtonObj = new ModernButton({
      scene: this.scene,
      x: x + width / 2,
      y: y + height - 30,
      text: i18next.t("ui:buttons.back"),
      style: "secondary",
      onClick: () => {
        if (this.onCloseCallback) this.onCloseCallback();
      },
    });
    this.closeButtonObj.setScrollFactor(0);
    if (this.uiLayer) this.uiLayer.add(this.closeButtonObj as unknown as Phaser.GameObjects.GameObject);
  }

  private updateAddFriendButton(x: number, width: number, baseElements: Phaser.GameObjects.GameObject[]) {
    if (this.addFriendHeaderBtn) {
      this.addFriendHeaderBtn.destroy();
      this.addFriendHeaderBtn = null;
    }
    if (this.activeTab === "FRIENDS") {
      const headerAddFriendX = x + width - 64;
      const headerAddFriendY = this.getWindowBounds()?.y! + 25;
      this.addFriendHeaderBtn = new ModernButton({
        scene: this.scene,
        x: headerAddFriendX,
        y: headerAddFriendY,
        text: i18next.t("ui:buttons.addFriend") || "ADD FRIEND",
        style: "success",
        size: "small",
        onClick: () => {
          this.showAddFriendInputWindow();
        },
      });
      this.addFriendHeaderBtn.setScrollFactor(0);
      baseElements.push(this.addFriendHeaderBtn);
      // Also add to container if it exists
      if (this.container) {
        this.container.add(this.addFriendHeaderBtn);
      }
    }
  }

  private updateAddFriendButtonForTabSwitch() {
    const bounds = this.getWindowBounds();
    if (!bounds) return;
    const { x, width } = bounds;

    if (this.addFriendHeaderBtn) {
      this.addFriendHeaderBtn.destroy();
      this.addFriendHeaderBtn = null;
    }

    if (this.activeTab === "FRIENDS" && this.container) {
      const headerAddFriendX = x + width - 64;
      const headerAddFriendY = bounds.y + 25;
      this.addFriendHeaderBtn = new ModernButton({
        scene: this.scene,
        x: headerAddFriendX,
        y: headerAddFriendY,
        text: i18next.t("ui:buttons.addFriend") || "ADD FRIEND",
        style: "success",
        size: "small",
        onClick: () => {
          this.showAddFriendInputWindow();
        },
      });
      this.addFriendHeaderBtn.setScrollFactor(0);
      this.container.add(this.addFriendHeaderBtn);
    }
  }

  private destroySquadTabContent() {
    if (this.shareBtn) {
      this.shareBtn.destroy();
      this.shareBtn = null;
    }
    if (this.leaveBtn) {
      this.leaveBtn.destroy();
      this.leaveBtn = null;
    }
    if (this.createBtn) {
      this.createBtn.destroy();
      this.createBtn = null;
    }
    if (this.joinBtn) {
      this.joinBtn.destroy();
      this.joinBtn = null;
    }
    if (this.squadCodeText) {
      this.squadCodeText.destroy();
      this.squadCodeText = null;
    }
  }

  private destroyFriendsTabContent() {
    // Friend buttons are part of tabContentContainer and will be destroyed with it
    // No additional cleanup needed as they're not stored as instance variables
  }

  private destroyRequestsTabContent() {
    // Requests tab - cleanup will be handled by container destruction
  }

  private destroyAllTabContent() {
    this.destroySquadTabContent();
    this.destroyFriendsTabContent();
    this.destroyRequestsTabContent();
  }

  private showTabContent() {
    if (this.tabContentContainer) {
      this.tabContentContainer.destroy();
      this.tabContentContainer = null;
    }
    // Clean up any buttons from the previous tab
    this.destroyAllTabContent();
    const bounds = this.getWindowBounds()!;
    const { x, y, width } = bounds;
    const contentLeftX = x + this.CONTENT_PADDING_X;
    const contentRightX = x + width - this.CONTENT_PADDING_X;
    const contentStartY = y + 60 + 48;
    const elements: Phaser.GameObjects.GameObject[] = [];
    if (this.activeTab === "SQUAD") {
      // Show squad code and members (existing logic)
      const storedSquadCode = this.getSquadCode();
      const storedMembers = this.getSquadMembers();
      if (storedSquadCode) {
        // Squad code display
        const containerX = contentLeftX;
        const containerY = contentStartY + 24;
        const containerWidth = 220;
        const containerHeight = 52;
        const borderWidth = 3;
        const borderColorHex = COLORS.GREEN;
        const borderColorNum = parseInt(borderColorHex.replace("#", ""), 16);
        const bgColor = 0x0a3a1a;
        const codeContainerBg = this.scene.add.rectangle(
          containerX + containerWidth / 2,
          containerY + containerHeight / 2,
          containerWidth,
          containerHeight,
          bgColor,
        );
        codeContainerBg.setStrokeStyle(borderWidth, borderColorNum);
        codeContainerBg.setScrollFactor(0);
        elements.push(codeContainerBg);
        const labelText = this.scene.add.text(
          containerX + containerWidth / 2,
          containerY - 8,
          "SQUAD CODE",
          {
            fontSize: "12px",
            color: borderColorHex,
            fontFamily: "Anton",
            align: "center",
          },
        ).setOrigin(0.5, 1);
        labelText.setScrollFactor(0);
        elements.push(labelText);
        this.squadCodeText = this.scene.add.text(
          containerX + containerWidth / 2,
          containerY + containerHeight / 2 - 2,
          storedSquadCode,
          {
            fontSize: "30px",
            color: borderColorHex,
            fontFamily: "Anton",
            align: "center",
          },
        ).setOrigin(0.5, 0.5);
        this.squadCodeText.setScrollFactor(0);
        elements.push(this.squadCodeText);
        let membersText = "Members: ";
        if (storedMembers.length > 0) {
          membersText += storedMembers.join(", ");
        }
        const membersLabel = this.scene.add.text(
          contentLeftX,
          containerY + containerHeight + 80,
          membersText,
          {
            fontSize: "14px",
            color: COLORS.WHITE,
            fontFamily: "Anton",
            align: "left",
          },
        ).setOrigin(0, 0);
        membersLabel.setScrollFactor(0);
        elements.push(membersLabel);
        const leaveButtonY = containerY + containerHeight + 40;
        this.shareBtn = new ModernButton({
          scene: this.scene,
          x: contentLeftX + 40,
          y: leaveButtonY,
          text: i18next.t("ui:buttons.share") || "SHARE",
          style: "success",
          onClick: () => {
            this.handleShareSquad();
          },
        });
        this.shareBtn.setScrollFactor(0);
        elements.push(this.shareBtn);
        this.leaveBtn = new ModernButton({
          scene: this.scene,
          x: contentLeftX + 120 + this.buttonSpacing,
          y: leaveButtonY,
          text: i18next.t("ui:buttons.leave") || "LEAVE",
          style: "danger",
          onClick: () => {
            this.handleLeaveSquad();
          },
        });
        this.leaveBtn.setScrollFactor(0);
        elements.push(this.leaveBtn);
      } else {
        // Show create and join buttons
        const titleText = this.scene.add.text(
          contentLeftX,
          contentStartY,
          "Create or Join a Squad",
          {
            fontSize: "18px",
            color: COLORS.YELLOW,
            fontFamily: "Anton",
            align: "left",
          },
        ).setOrigin(0, 0);
        titleText.setScrollFactor(0);
        elements.push(titleText);
        const createBtnY = contentStartY + 80;
        this.createBtn = new ModernButton({
          scene: this.scene,
          x: 0,
          y: createBtnY,
          text: i18next.t("ui:buttons.create") || "CREATE",
          style: "success",
          onClick: () => {
            this.handleCreateSquad();
          },
        });
        this.createBtn.setX(contentLeftX + this.createBtn.buttonWidth / 2);
        this.createBtn.setScrollFactor(0);
        elements.push(this.createBtn);
        const joinBtnY = createBtnY + 48;
        this.joinBtn = new ModernButton({
          scene: this.scene,
          x: 0,
          y: joinBtnY,
          text: i18next.t("ui:buttons.join") || "JOIN",
          style: "primary",
          onClick: () => {
            this.showSquadCodeInputWindow();
          },
        });
        this.joinBtn.setX(contentLeftX + this.joinBtn.buttonWidth / 2);
        this.joinBtn.setScrollFactor(0);
        elements.push(this.joinBtn);
      }
    } else if (this.activeTab === "FRIENDS") {
      // Request friends list from server only once
      if (!this.friendsListRequested) {
        this.friendsListRequested = true;
        window.socket?.emit("friends-list-request", (response: { success: boolean; friends: Array<FriendInfo> }) => {
          if (response.success) {
            this.friendsList = response.friends;
            if (!this.visible) {
              this.show();
            } else if (this.activeTab === "FRIENDS") {
              // Refresh UI to display the friends list
              this.showTabContent();
            }
          }
        });
      }

      // Show friend detail view or friends list
      if (this.selectedFriend) {
        // Detail view for selected friend
        const friend = this.selectedFriend;
        const detailStartY = contentStartY;

        // Friend name
        const nameText = this.scene.add.text(
          contentLeftX,
          detailStartY,
          friend.name,
          {
            fontSize: "20px",
            color: COLORS.YELLOW,
            fontFamily: "Anton",
            align: "left",
          },
        ).setOrigin(0, 0);
        nameText.setScrollFactor(0);
        elements.push(nameText);

        // Kill Streak stat
        const killStreakY = detailStartY + 50;
        const killStreakLabel = this.scene.add.text(
          contentLeftX,
          killStreakY,
          "Kill Streak:",
          {
            fontSize: "14px",
            color: COLORS.WHITE,
            fontFamily: "Anton",
            align: "left",
          },
        ).setOrigin(0, 0);
        killStreakLabel.setScrollFactor(0);
        elements.push(killStreakLabel);

        const killStreakValue = this.scene.add.text(
          contentLeftX + 150,
          killStreakY,
          friend.killStreak.toString(),
          {
            fontSize: "14px",
            color: COLORS.GREEN,
            fontFamily: "Anton",
            align: "left",
          },
        ).setOrigin(0, 0);
        killStreakValue.setScrollFactor(0);
        elements.push(killStreakValue);

        // Extractions stat
        const extractionsY = killStreakY + 25;
        const extractionsLabel = this.scene.add.text(
          contentLeftX,
          extractionsY,
          "Extractions:",
          {
            fontSize: "14px",
            color: COLORS.WHITE,
            fontFamily: "Anton",
            align: "left",
          },
        ).setOrigin(0, 0);
        extractionsLabel.setScrollFactor(0);
        elements.push(extractionsLabel);

        const extractionsValue = this.scene.add.text(
          contentLeftX + 150,
          extractionsY,
          friend.nrExtractions.toString(),
          {
            fontSize: "14px",
            color: COLORS.GREEN,
            fontFamily: "Anton",
            align: "left",
          },
        ).setOrigin(0, 0);
        extractionsValue.setScrollFactor(0);
        elements.push(extractionsValue);

        // Online status
        const statusY = extractionsY + 25;
        const statusLabel = this.scene.add.text(
          contentLeftX,
          statusY,
          "Status:",
          {
            fontSize: "14px",
            color: COLORS.WHITE,
            fontFamily: "Anton",
            align: "left",
          },
        ).setOrigin(0, 0);
        statusLabel.setScrollFactor(0);
        elements.push(statusLabel);

        const statusValue = this.scene.add.text(
          contentLeftX + 150,
          statusY,
          friend.isOnline ? "Online" : "Offline",
          {
            fontSize: "14px",
            color: friend.isOnline ? COLORS.GREEN : COLORS.GRAY,
            fontFamily: "Anton",
            align: "left",
          },
        ).setOrigin(0, 0);
        statusValue.setScrollFactor(0);
        elements.push(statusValue);

        // Back and Delete buttons
        const buttonY = statusY + 56;
        const backBtn = new ModernButton({
          scene: this.scene,
          x: contentLeftX + 38,
          y: buttonY,
          text: i18next.t("ui:buttons.back") || "BACK",
          style: "primary",
          onClick: () => {
            this.selectedFriend = null;
            this.showTabContent();
          },
        });
        backBtn.setScrollFactor(0);
        elements.push(backBtn);

        const deleteBtn = new ModernButton({
          scene: this.scene,
          x: contentLeftX + 42 + 90,
          y: buttonY,
          text: i18next.t("ui:buttons.delete") || "DELETE",
          style: "danger",
          onClick: () => {
            this.handleDeleteFriend(friend.accountId);
          },
        });
        deleteBtn.setScrollFactor(0);
        elements.push(deleteBtn);
      } else {
        // Friends list view
        const friends = this.getFriends();
        const friendListStartY = contentStartY + 16;
        const friendSpacing = 40;

        if (friends.length === 0) {
          const noFriendsText = this.scene.add.text(
            contentLeftX,
            contentStartY,
            "No friends yet.",
            {
              fontSize: "18px",
              color: COLORS.YELLOW,
              fontFamily: "Anton",
              align: "left",
            },
          ).setOrigin(0, 0);
          noFriendsText.setScrollFactor(0);
          elements.push(noFriendsText);
        } else {
          friends.forEach((friend, idx) => {
            const friendY = friendListStartY + idx * friendSpacing;

            // Friend name as a small button - green if online, gray if offline
            const friendBtn = new ModernButton({
              scene: this.scene,
              x: contentLeftX + 42,
              y: friendY,
              text: friend.name,
              style: friend.isOnline ? "success" : "primary",
              size: "small",
              onClick: () => {
                this.handleShowFriendDetail(friend);
              },
            });
            friendBtn.setScrollFactor(0);
            elements.push(friendBtn);
          });
        }
      }
    } else if (this.activeTab === "REQUESTS") {
      // Request friend requests from server only once
      if (!this.friendRequestsListRequested) {
        this.friendRequestsListRequested = true;
        window.socket?.emit("friend-requests-request", (response: { success: boolean; requests: Array<FriendRequest> }) => {
          if (response.success) {
            this.friendRequestsList = response.requests;
            if (!this.visible) {
              this.show();
            } else if (this.activeTab === "REQUESTS") {
              // Refresh UI to display the requests list
              this.showTabContent();
            }
          }
        });
      }

      // REQUESTS tab - display friend requests
      const requestsList = this.friendRequestsList;
      if (!requestsList || requestsList.length === 0) {
        const noRequestsText = this.scene.add.text(
          contentLeftX,
          contentStartY,
          "No friend requests",
          {
            fontSize: "18px",
            color: COLORS.GRAY,
            fontFamily: "Anton",
            align: "left",
          },
        ).setOrigin(0, 0);
        noRequestsText.setScrollFactor(0);
        elements.push(noRequestsText);
      } else {
        const requestSpacing = 50;
        requestsList.forEach((request, idx) => {
          const requestY = contentStartY + idx * requestSpacing;
          
          // Request name
          const requestNameText = this.scene.add.text(
            contentLeftX,
            requestY,
            request.name,
            {
              fontSize: "16px",
              color: COLORS.WHITE,
              fontFamily: "Anton",
            },
          ).setOrigin(0, 0);
          requestNameText.setScrollFactor(0);
          elements.push(requestNameText);
          
          // Accept button (green checkmark)
          const acceptBtn = new ModernButton({
            scene: this.scene,
            x: contentRightX - 50,
            y: requestY + 8,
            text: "✓",
            style: "success",
            size: "small",
            onClick: () => {
              this.handleAcceptFriendRequest(request.accountId);
            },
          });
          acceptBtn.setScrollFactor(0);
          elements.push(acceptBtn);
          
          // Reject button (red X)
          const rejectBtn = new ModernButton({
            scene: this.scene,
            x: contentRightX - 15,
            y: requestY + 8,
            text: "✕",
            style: "danger",
            size: "small",
            onClick: () => {
              this.handleRejectFriendRequest(request.accountId);
            },
          });
          rejectBtn.setScrollFactor(0);
          elements.push(rejectBtn);
        });
      }
    }
    this.tabContentContainer = this.scene.add.container(0, 0, elements);
    this.tabContentContainer.setDepth(100002);
    this.tabContentContainer.setScrollFactor(0);
    if (this.container) {
      this.container.add(this.tabContentContainer);
    }
  }

  private handleCreateSquad() {
    trackEvent("squad-created");
    window.socket?.emit("squad-create");
  }

  private handleLeaveSquad() {
    // Clear squad data immediately from client
    this.clearSquadCode();
    this.clearSquadMembers();
    trackEvent("squad-leave");
    // Notify server
    window.socket?.emit("squad-leave");
    // Refresh tab content to show create/join buttons
    this.showTabContent();
  }

  private async handleShareSquad() {
    trackEvent("squad-code-shared");
    const squadCode = this.getSquadCode();
    if (squadCode) {
      await shareSquad(squadCode);
    }
  }

  getSquadCode(): string | null {
    try {
      return localStorage.getItem(SQUAD_CODE_STORAGE_KEY);
    } catch {
      return null;
    }
  }

  private setSquadCode(code: string) {
    try {
      localStorage.setItem(SQUAD_CODE_STORAGE_KEY, code);
    } catch (err) {
      console.error("Failed to save squad code to localStorage:", err);
    }
  }

  private clearSquadCode() {
    try {
      localStorage.removeItem(SQUAD_CODE_STORAGE_KEY);
    } catch (err) {
      console.error("Failed to clear squad code from localStorage:", err);
    }
  }

  private getSquadMembers(): string[] {
    try {
      const members = localStorage.getItem(SQUAD_MEMBERS_STORAGE_KEY);
      return members ? JSON.parse(members) : [];
    } catch {
      return [];
    }
  }

  private setSquadMembers(members: string[]) {
    try {
      localStorage.setItem(SQUAD_MEMBERS_STORAGE_KEY, JSON.stringify(members));
    } catch (err) {
      console.error("Failed to save squad members to localStorage:", err);
    }
  }

  private clearSquadMembers() {
    try {
      localStorage.removeItem(SQUAD_MEMBERS_STORAGE_KEY);
    } catch (err) {
      console.error("Failed to clear squad members from localStorage:", err);
    }
  }

  storeSquadCode(code: string, members: string[] = []) {
    this.setSquadCode(code);
    this.setSquadMembers(members);
    if (this.visible) {
      this.show(this.onCloseCallback ?? undefined);
    }
  }

  clearAndRefresh() {
    this.clearSquadCode();
    this.clearSquadMembers();
    if (this.visible) {
      this.show(this.onCloseCallback ?? undefined);
    }
  }

  private showSquadCodeInputWindow() {
    this.hideSquadCodeInputWindow();

    const gameWidth = this.scene.scale.width;
    const gameHeight = this.scene.scale.height;

    // Full screen semi-transparent background
    const bgOverlay = this.scene.add.rectangle(
      gameWidth / 2,
      gameHeight / 2,
      gameWidth,
      gameHeight,
      hexToPhaser(COLORS.BLACK),
      0.8,
    );
    bgOverlay.setOrigin(0.5);
    bgOverlay.setScrollFactor(0);

    // Center panel
    const panelWidth = Math.min(400, gameWidth - 32);
    const panelHeight = 250;
    const panelX = gameWidth / 2;
    const panelY = gameHeight / 2;
    const cornerRadius = 12;

    const panelBg = this.scene.add.graphics();
    // Background
    panelBg.fillStyle(hexToPhaser(COLORS.BG), 0.92);
    panelBg.fillRoundedRect(
      panelX - panelWidth / 2,
      panelY - panelHeight / 2,
      panelWidth,
      panelHeight,
      cornerRadius,
    );
    // Outer border (black)
    const outerBorderWidth = 4;
    panelBg.lineStyle(outerBorderWidth, hexToPhaser(COLORS.BLACK), 0.8);
    panelBg.strokeRoundedRect(
      panelX - panelWidth / 2,
      panelY - panelHeight / 2,
      panelWidth,
      panelHeight,
      cornerRadius,
    );
    // Inner border (darker BG color)
    const innerBorderWidth = 4;
    const inset = (outerBorderWidth + innerBorderWidth) / 2;
    const darkerBG = darkenColor(hexToPhaser(COLORS.BG), 0.3);
    panelBg.lineStyle(innerBorderWidth, darkerBG, 0.9);
    panelBg.strokeRoundedRect(
      panelX - panelWidth / 2 + inset,
      panelY - panelHeight / 2 + inset,
      panelWidth - inset * 2,
      panelHeight - inset * 2,
      Math.max(cornerRadius - inset, 4),
    );
    panelBg.setScrollFactor(0);

    // Title
    const title = this.scene.add
      .text(panelX, panelY - panelHeight / 2 + 24, "Join Squad", {
        fontSize: "24px",
        color: COLORS.WHITE,
        fontFamily: "Anton",
        align: "center",
      })
      .setOrigin(0.5, 0);
    title.setScrollFactor(0);

    // Calculate input field dimensions (full width with padding)
    const inputPadding = 24;
    const inputWidth = panelWidth - inputPadding * 2;
    const inputHeight = 56;
    const inputX = panelX;
    const inputY = panelY - 10;

    // Create HTML input element for native keyboard support
    this.htmlInputElement = document.createElement("input");
    this.htmlInputElement.type = "text";
    this.htmlInputElement.placeholder = "Enter squad code";
    this.htmlInputElement.style.position = "absolute";
    this.htmlInputElement.maxLength = 6;

    // Get canvas position and calculate absolute position
    const canvas = this.scene.game.canvas;
    const canvasRect = canvas.getBoundingClientRect();

    // Calculate scale factor between game coordinates and screen coordinates
    const scaleX = canvasRect.width / gameWidth;
    const scaleY = canvasRect.height / gameHeight;

    // Convert game coordinates to screen coordinates
    const screenX = canvasRect.left + inputX * scaleX;
    const screenY = canvasRect.top + inputY * scaleY;
    const screenWidth = inputWidth * scaleX;
    const screenHeight = inputHeight * scaleY;

    this.htmlInputElement.style.left = `${screenX - screenWidth / 2}px`;
    this.htmlInputElement.style.top = `${screenY - screenHeight / 2}px`;
    this.htmlInputElement.style.width = `${screenWidth}px`;
    this.htmlInputElement.style.height = `${screenHeight}px`;
    this.htmlInputElement.style.fontSize = `${22 * Math.min(scaleX, scaleY)}px`;
    this.htmlInputElement.style.fontFamily = "Anton, sans-serif";
    this.htmlInputElement.style.backgroundColor = `#${hexToPhaser(COLORS.GRAY)
      .toString(16)
      .padStart(6, "0")}`;
    this.htmlInputElement.style.color = COLORS.WHITE;
    this.htmlInputElement.style.border = `3px solid ${COLORS.WHITE}`;
    this.htmlInputElement.style.padding = "12px";
    this.htmlInputElement.style.textAlign = "center";
    this.htmlInputElement.style.outline = "none";
    this.htmlInputElement.style.zIndex = "100002";
    this.htmlInputElement.style.boxSizing = "border-box";
    this.htmlInputElement.style.textTransform = "uppercase";
    document.body.appendChild(this.htmlInputElement);

    // Buttons on same row
    const btnGap = 16;
    const btnY = panelY + panelHeight / 2 - 40;

    // Cancel button (left side)
    const cancelButton = new ModernButton({
      scene: this.scene,
      x: 0, // Will be positioned after creation
      y: btnY,
      text: i18next.t("ui:buttons.cancel") || "CANCEL",
      style: "danger",
      onClick: () => {
        this.hideSquadCodeInputWindow();
      },
    });
    cancelButton.setScrollFactor(0);

    // Join button (right side) - initially disabled
    const joinButton = new ModernButton({
      scene: this.scene,
      x: 0, // Will be positioned after creation
      y: btnY,
      text: i18next.t("ui:buttons.join") || "JOIN",
      style: "success",
      disabled: true,
      onClick: () => {
        const squadCode = this.htmlInputElement?.value.trim() || "";
        if (squadCode) {
          this.hideSquadCodeInputWindow();
          trackEvent("squad-joined");
          this.onJoinSubmitted?.();
          window.socket?.emit("squad-join", { squadCode });
        }
      },
    });
    joinButton.setScrollFactor(0);

    // Position buttons
    const totalWidth =
      (cancelButton as any).buttonWidth +
      btnGap +
      (joinButton as any).buttonWidth;
    const cancelBtnX =
      panelX - totalWidth / 2 + (cancelButton as any).buttonWidth / 2;
    const joinBtnX =
      panelX + totalWidth / 2 - (joinButton as any).buttonWidth / 2;
    cancelButton.setX(cancelBtnX);
    joinButton.setX(joinBtnX);

    // Validation function
    const updateSquadCodeValidation = () => {
      const squadCode = this.htmlInputElement?.value || "";
      const isValid = squadCode.trim().length === 6;

      // Update join button appearance
      joinButton.setDisabled(!isValid);
    };

    // Listen to input changes
    this.htmlInputElement.addEventListener("input", () => {
      // Convert to uppercase
      if (this.htmlInputElement) {
        this.htmlInputElement.value = this.htmlInputElement.value.toUpperCase();
      }
      updateSquadCodeValidation();
    });

    // Handle Enter key to submit
    this.htmlInputElement.addEventListener(
      "keydown",
      (event: KeyboardEvent) => {
        if (event.key === "Enter") {
          const squadCode = this.htmlInputElement?.value.trim() || "";
          if (squadCode) {
            this.hideSquadCodeInputWindow();
            trackEvent("squad_joined");
            this.onJoinSubmitted?.();
            window.socket?.emit("squad-join", { squadCode });
          }
        }
      },
    );

    // Auto-focus the input
    setTimeout(() => {
      if (this.htmlInputElement) {
        this.htmlInputElement.focus();
      }
    }, 100);

    // Create container
    const containerElements: Phaser.GameObjects.GameObject[] = [
      bgOverlay,
      panelBg,
      title,
      cancelButton,
      joinButton,
    ];

    this.squadCodeInputContainer = this.scene.add.container(
      0,
      0,
      containerElements,
    );
    this.squadCodeInputContainer.setDepth(100001);
    this.squadCodeInputContainer.setVisible(true);

    // Add squad code input container to UI layer
    this.uiLayer.add(this.squadCodeInputContainer);
  }

  private hideSquadCodeInputWindow() {
    if (this.htmlInputElement) {
      this.htmlInputElement.remove();
      this.htmlInputElement = null;
    }

    if (this.squadCodeInputContainer) {
      this.squadCodeInputContainer.destroy();
      this.squadCodeInputContainer = null;
    }
  }

  showSquadErrorPopup(errorMessage: string) {
    if (!this.scene) return;

    const gameWidth = this.scene.scale.width;
    const gameHeight = this.scene.scale.height;

    // Full screen semi-transparent background
    const bgOverlay = this.scene.add.rectangle(
      gameWidth / 2,
      gameHeight / 2,
      gameWidth,
      gameHeight,
      hexToPhaser(COLORS.BLACK),
      0.8,
    );
    bgOverlay.setOrigin(0.5);
    bgOverlay.setScrollFactor(0);

    // Center panel
    const panelWidth = Math.min(400, gameWidth - 32);
    const panelHeight = 200;
    const panelX = gameWidth / 2;
    const panelY = gameHeight / 2;
    const cornerRadius = 12;

    const panelBg = this.scene.add.graphics();
    panelBg.fillStyle(hexToPhaser(COLORS.BG), 0.92);
    panelBg.fillRoundedRect(
      panelX - panelWidth / 2,
      panelY - panelHeight / 2,
      panelWidth,
      panelHeight,
      cornerRadius,
    );
    const outerBorderWidth = 4;
    panelBg.lineStyle(outerBorderWidth, hexToPhaser(COLORS.BLACK), 0.8);
    panelBg.strokeRoundedRect(
      panelX - panelWidth / 2,
      panelY - panelHeight / 2,
      panelWidth,
      panelHeight,
      cornerRadius,
    );
    const innerBorderWidth = 4;
    const inset = (outerBorderWidth + innerBorderWidth) / 2;
    const darkerBG = darkenColor(hexToPhaser(COLORS.BG), 0.3);
    panelBg.lineStyle(innerBorderWidth, darkerBG, 0.9);
    panelBg.strokeRoundedRect(
      panelX - panelWidth / 2 + inset,
      panelY - panelHeight / 2 + inset,
      panelWidth - inset * 2,
      panelHeight - inset * 2,
      Math.max(cornerRadius - inset, 4),
    );
    panelBg.setScrollFactor(0);

    // Title
    const title = this.scene.add
      .text(panelX, panelY - panelHeight / 2 + 24, "Error", {
        fontSize: "24px",
        color: COLORS.RED,
        fontFamily: "Anton",
        align: "center",
      })
      .setOrigin(0.5, 0);
    title.setScrollFactor(0);

    // Error message
    const errorText = this.scene.add
      .text(panelX, panelY - 20, errorMessage, {
        fontSize: "16px",
        color: COLORS.WHITE,
        fontFamily: "Anton",
        align: "center",
        wordWrap: { width: panelWidth - 48 },
      })
      .setOrigin(0.5, 0.5);
    errorText.setScrollFactor(0);

    // OK button
    const okButton = new ModernButton({
      scene: this.scene,
      x: panelX,
      y: panelY + panelHeight / 2 - 30,
      text: i18next.t("ui:buttons.ok") || "OK",
      style: "primary",
      onClick: () => {
        errorPopupContainer.destroy();
        // Show the squad code input window again
        this.showSquadCodeInputWindow();
      },
    });
    okButton.setScrollFactor(0);

    // Create container
    const errorPopupContainer = this.scene.add.container(0, 0, [
      bgOverlay,
      panelBg,
      title,
      errorText,
      okButton,
    ]);
    errorPopupContainer.setDepth(100002);
    errorPopupContainer.setVisible(true);
    this.uiLayer.add(errorPopupContainer);
  }

  hide() {
    this.hideSquadCodeInputWindow();
    if (this.container) {
      this.container.destroy();
      this.container = null;
    }
    if (this.tabBar) {
      this.tabBar.destroy();
      this.tabBar = null;
    }
    if (this.tabContentContainer) {
      this.tabContentContainer.destroy();
      this.tabContentContainer = null;
    }
    // Destroy all tab content and their buttons
    this.destroyAllTabContent();
    if (this.addFriendHeaderBtn) {
      this.addFriendHeaderBtn.destroy();
      this.addFriendHeaderBtn = null;
    }
    if (this.closeButtonObj) {
      (this.closeButtonObj as unknown as Phaser.GameObjects.GameObject).destroy();
      this.closeButtonObj = null;
    }
    this.onCloseCallback = null;
    this.friendsListRequested = false;
    this.friendRequestsListRequested = false;

    this.visible = false;
  }

  isVisible(): boolean {
    return this.visible;
  }

  /**
   * Returns the number of pending friend requests
   */
  public getPendingFriendRequestsCount(): number {
    return Array.isArray(this.friendRequestsList) ? this.friendRequestsList.length : 0;
  }
}
